import axios from 'axios';
import { expandCollectionTags } from '../../../Util/collections';
import { serverClusterSort } from "../../../Util/clusterSort";
import constants from "../../../Util/constants";
import { getStorageValue } from "../../../Util/general";
import { matcherForFilterValue } from "../../../Util/amenityTaxonomy";
import { readStoredPriceItems, isPriceDefaulted, priceRequestBounds } from "../../../Util/priceLabel";
import { instantOnlyOn, isInstantListing } from "../../../Util/instantBook";
import moment from "moment";
import { currentSortBy } from "../../../Util/searchSort";
import { normalizeOriginHub } from "../../../Util/originHub";
//import default_res from '../../../Util/guesty_listing_defualt_res.json';
const log = require("loglevel").getLogger("PropertyService");
log.setLevel("debug");

// Debug logging gate — search responses are huge; logging them measurably
// slowed rendering with DevTools open. Flip to true when debugging search.
const DEBUG_LOG = false;
const dlog = (...args) => { if (DEBUG_LOG) console.log(...args); };


const safeParseArray = (raw) => {
	if (!raw) return [];
	try {
		const v = JSON.parse(raw);
		return Array.isArray(v) ? v : [];
	} catch {
		return [];
	}
};

class SearchSupersededError extends Error {
	constructor() {
		super('Search request superseded');
		this.name = 'SearchSupersededError';
	}
}

const SEARCH_CACHE_TTL_MS = 5 * 60 * 1000;
const SEARCH_CACHE_MAX_ENTRIES = 50;
const searchCache = new Map(); // queryString -> { data, expiresAt }
let currentSearchAbortController = null;
let latestSearchSequence = 0;

const cloneSearchResult = (res) => ({
	...res,
	listings: Array.isArray(res?.listings) ? res.listings.map((l) => ({ ...l })) : [],
});

const evictExpired = (now) => {
	for (const [k, v] of searchCache) {
		if (v.expiresAt <= now) searchCache.delete(k);
	}
	while (searchCache.size > SEARCH_CACHE_MAX_ENTRIES) {
		searchCache.delete(searchCache.keys().next().value);
	}
};

export const __clearSearchCache = () => searchCache.clear();

// Cross-hub dedupe key, still used by the MAP viewport merge (the paginated
// search merge moved server-side). Strips the channel prefix so the same villa
// from either hub collapses to one marker.
const mergeBaseId = (id) => String(id || '').replace(/^(G-|RU-|sh-ru-|sh-bp-)/, '');
const mergeDedupeKey = (item) => {
	const L = item?.listing || {};
	// BookingPal ids are per-hub sequences (VTHub's BP-12 ≠ SHub's BP-12), so
	// the same physical property arrives from both sources with DIFFERENT ids.
	// Key BP items on title+city instead — the hubs' own BP uniqueness rule is
	// title+property-manager, so this is at least as strict cross-source.
	// (Deliberately NOT adding BP- to mergeBaseId: stripped sequence numbers
	// would collide across hubs for unrelated properties.)
	if (item?.channelSource === 'BP') {
		const title = (L.title || '').trim().toLowerCase();
		const city = (L.address?.city || '').trim().toLowerCase();
		return `bp||${title}||${city}`;
	}
	const id = L._id || L.id || '';
	const title = (L.title || '').trim().toLowerCase();
	return `${mergeBaseId(id)}||${title}`;
};

// Search identity: every param except pagination (so all client pages share one entry).

// ---- Best-effort tag ranking -------------------------------------------------
// Guests' amenity/musthave picks are matched ANY-side ($in) for recall, then
// results are RANKED by how many of the picked tags each listing actually has
// (an AND of all tags is usually unsatisfiable — e.g. Beach Front+Pool+Sea View+
// Private Beach = 0 listings hub-wide). Matching mirrors the hubs: case-
// insensitive, space ≈ hyphen ≈ nothing ("Beach Front" hits "Beachfront").
// A matcher per picked value resolves the value to its canonical facet (see
// Util/amenityTaxonomy) and tests a listing's amenities against ALL that facet's
// known raw variants — so "Beach Front" actually matches "Beachfront",
// "location beachfront", etc. Unknown picks fall back to flexible substring matching.
const buildTagMatchers = (values) => (values || []).map((v) => matcherForFilterValue(v));

// Recover the picked tags from an already-built filters param (loadMergedSearch
// only receives params, not the original selections).
const tagMatchersFromFilters = (filtersStr) => {
	try {
		const arr = JSON.parse(filtersStr || '[]');
		const f = arr.find((x) => x?.field === 'amenities' && x?.operator === '$in');
		return buildTagMatchers(Array.isArray(f?.value) ? f.value : []);
	} catch (e) {
		return [];
	}
};

// Strict mode ("Exact filter matches only" header toggle): the query still goes
// out ANY-match for recall, but only listings carrying EVERY picked tag are kept
// client-side, and the zero-result relax ladder is skipped.
const strictFiltersOn = () => localStorage.getItem('strictFilters') === 'true';
const matchesAllTags = (it, matchers) => matchers.every((m) => m.test(it?.listing?.amenities));

// Build the hub `available` param — the single source of truth for all three call sites
// (loadProperties / toggleOnDemandandloadProperties / buildFavoriteSearchParams), which used
// to be near-identical copies. A flexible search WIDENS the search window (± flexDays) while
// `nights` keeps telling the hub how long the guest actually wants to stay, so a wide window
// doesn't exclude villas whose maxNights < window span (VTHub listings.js parseAvailableParam
// reads `nights`; SHub ignores it). `nights` is emitted ONLY for a flexible search — a plain
// exact pick omits it so the request stays byte-for-byte identical to before this feature.
// Inputs are the already-formatted YYYY-MM-DD strings (or 0) the callers compute.
// `usePrice: false` builds the param without any price budget (favourites does this when
// the stored price is only the seeded default — see buildFavoriteSearchParams).
// Exported so a test can assert the outgoing param.
// A search WITHOUT dates has no `available` param (buildAvailableParam returns '' below), so
// the ticked Budget brackets travel on their own as `budget`, and both hubs filter them on the
// card's price per night: the base nightly price, marked up, in USD (VTHub/SHub
// api/utils/priceBudget.js). With dates they go inside `available`. '' when no bracket is
// ticked, so nothing is filtered. The shape is Util/priceLabel priceRequestBounds.
export const buildBudgetParam = () => {
	try {
		const bounds = priceRequestBounds(readStoredPriceItems());
		return bounds ? JSON.stringify(bounds) : '';
	} catch (e) {
		return '';
	}
};

export const buildAvailableParam = (dateFrom, dateTo, guests, flexDays = 0, nightsOverride = 0, { usePrice = true } = {}) => {
	if (!dateFrom || !dateTo || dateFrom === 'null' || dateTo === 'null') return '';
	const span = Math.max(0, Math.round((moment(dateTo) - moment(dateFrom)) / 86400000));
	const nights = nightsOverride > 0 ? nightsOverride : span;
	// A WIDENED check-in is clamped to today (never search the past); an exact pick (flexDays 0)
	// is passed through untouched so its request stays byte-identical even for a stale past date.
	// The Flexible tab already clamps its month window to tomorrow in the modal.
	const from = flexDays > 0
		? moment.max(moment(dateFrom).subtract(flexDays, 'days'), moment().startOf('day'))
		: moment(dateFrom);
	const checkIn = from.format('YYYY-MM-DD');
	const checkOut = moment(dateTo).add(flexDays, 'days').format('YYYY-MM-DD');
	// Number() so minOccupancy serialises UNQUOTED (":2", not ":\"2\"") — byte-identical to the
	// old string-concatenated param whether guests arrived as a number or a string.
	const base = { checkIn, checkOut, minOccupancy: Number(guests) };
	// Budget brackets: the hubs filter on the card's stay total server-side (correct pagination) —
	// (basePriceUSD || basePrice) x markup x nights, what enhanceAndSortListings shows (actions.js).
	// The brackets are per night, so they are sent multiplied by the nights of the stay: the same
	// `nights` the hub multiplies by (the picked stay, also when the window is widened).
	// Sent only when a bracket is ticked.
	// Without dates they travel as `budget` instead (buildBudgetParam).
	try {
		const bounds = usePrice ? priceRequestBounds(readStoredPriceItems(), nights) : null;
		if (bounds) Object.assign(base, bounds);
	} catch (e) { /* no budget */ }
	const isFlexible = flexDays > 0 || nightsOverride > 0;
	if (!isFlexible) return JSON.stringify(base);
	// "± N days": the hub is sent the WIDENED window, so it cannot tell which dates were picked.
	// The card is priced for the picked dates, and `stayFrom` names their check-in so that the
	// hub's price sort orders by that price (VTHub api/utils/stayPriceSort.js). Not on the
	// Flexible tab: its dates are a month to look in, and its card shows base x nights.
	const picked = flexDays > 0 && nights === span ? { stayFrom: moment(dateFrom).format('YYYY-MM-DD') } : {};
	return JSON.stringify({ ...base, nights, ...picked });
};
// The request-affecting flexible inputs the modal mirrors to localStorage (see SearchDatesModal).
const readFlexInputs = () => ({
	flexDays: parseInt(localStorage.getItem('searchFlexDays')) || 0,
	stayNights: parseInt(localStorage.getItem('searchStayNights')) || 0,
});

// Stamp listing._tagMatch = { count, total, matched } on every item so the card
// badge ("Matches 3/5 filters") and every downstream sort can read it.
const attachTagMatches = (listings, matchers) => {
	if (!matchers.length || !Array.isArray(listings)) return;
	for (const it of listings) {
		if (!it) continue;
		const amens = it?.listing?.amenities;
		const matched = matchers.filter((m) => m.test(amens)).map((m) => m.value);
		it.listing = { ...(it.listing || {}), _tagMatch: { count: matched.length, total: matchers.length, matched } };
	}
};

// Stable sort (highest match count first) — within equal counts the incoming
// order (price sort etc.) is preserved.
const rankByTagMatches = (listings) => {
	if (!Array.isArray(listings)) return;
	listings.sort((a, b) => (b?.listing?._tagMatch?.count || 0) - (a?.listing?._tagMatch?.count || 0));
};
// -----------------------------------------------------------------------------

// Server-side SHUB+VTHUB merge — one request, exact counts, whole-catalogue
// dedupe. Replaces the windowed in-browser merge below (kept behind
// constants.USE_SERVER_MERGE until this is proven in production).
// `sourceFilter` (admin VT/SH chips) is forwarded so the hub can restrict the
// merge; strict tag filtering stays client-side exactly as before.
const loadServerMergedSearch = async (params, reqInstance, sourceFilter = 'all', strict = false) => {
	const p = { ...params };
	if (sourceFilter && sourceFilter !== 'all') p.sourceFilter = sourceFilter;
	const qs = Object.keys(p).map((k) => `${k}=${encodeURIComponent(p[k])}`).join('&');
	const res = await reqInstance
		.get(`${constants.SHUB_URL}/local/listings-search-merged?${qs}`)
		.then((r) => r.data);

	let listings = Array.isArray(res?.listings) ? res.listings : [];
	// Belt-and-braces for the Instant Book toggle: the hub already filtered, but a
	// card the payload can prove is request-to-book never shows under the toggle.
	// Unknown (no effective mode on the card) is KEPT — dropping it would blank the
	// page against a hub that filtered correctly but shipped no badge data.
	if (params.instantBook) {
		listings = listings.filter((it) => isInstantListing(it) !== false);
	}
	const matchers = tagMatchersFromFilters(params.filters);
	if (strict && matchers.length) {
		listings = listings.filter((it) => matchesAllTags(it, matchers));
	}
	attachTagMatches(listings, matchers);
	rankByTagMatches(listings);

	const allCount = Number(res?.allCount) || listings.length;
	localStorage.setItem('count', allCount);
	localStorage.setItem('wishlist', listings.length > 0 ? false : true);
	localStorage.setItem('withondemandpropertycount', res?.onDemandCount || 0);
	localStorage.setItem('allpropertycount', allCount);

	return cloneSearchResult({
		listings,
		count: allCount,
		allCount,
		onDemandCount: res?.onDemandCount || 0,
		vtCount: res?.sources?.vt || 0,
		shCount: res?.sources?.sh || 0,
	});
};


// A hub copy the merged search would show: listed and Approved. A declined or
// unlisted copy is still returned by /local/listings, so an id held by BOTH hubs
// (G-/RU-/BP- exist on each) must not open the dead copy when the other is live.
export const isLiveHubCopy = (res) => {
	const it = res?.listings?.[0];
	if (!it) return false;
	const status = it.xdata?.status;
	return it.isListed !== false && (!status || status === 'Approved');
};

// opts.hub ('SHUB'/'VTHUB'): the hub the search card came from (its _source) —
// tried first, so an in-app open stays on the hub that was quoted in search.
export const loadProperty = async (propertyId, opts = {}) => {
	log.debug("PropertyService -> loadProperty -> Enter");

	const token2 = constants.SHUB_STATIC_TOKEN;

	const reqInstance = axios.create({
		headers: {
			Authorization: `Bearer ${token2}`
		}
	});

	const filter_ids = '[{"field":"ids", "operator":"$in", "value": ["' + propertyId + '"]}]';

	const params = {
		filters: filter_ids,
		limit: 1,
		skip: 0,

	};
	// q: localStorage.getItem("destination"),
	//available:available,
	const queryString = Object.keys(params).map(key => key + '=' + params[key]).join('&');

	// Origin-aware, like loadfullcalendar + the search merge: SHUB ids start with
	// 'sh' (sh-ru-/sh-bp-) and live on SHUB_SOURCE_URL, everything else on VTHUB.
	// The old code queried VTHUB only, so a deep-link to a SHUB id returned empty
	// and the detail page rendered blank. Stamp _source on each returned listing
	// (mirrors loadMergedSearch) so the detail page's quote/calendar hub routing —
	// which keys off property._source — works for deep-links too.
	const hinted = normalizeOriginHub(opts.hub);
	const isShubId = hinted ? hinted === 'SHUB' : String(propertyId).substring(0, 2) === 'sh';
	const primaryHub = isShubId ? constants.SHUB_SOURCE_URL : constants.SHUB_URL;
	const fallbackHub = isShubId ? constants.SHUB_URL : constants.SHUB_SOURCE_URL;

	const stampSource = (res, hubUrl) => {
		if (!res || !Array.isArray(res.listings)) return res;
		const srcLabel = hubUrl === constants.SHUB_SOURCE_URL ? 'SHUB' : 'VTHUB';
		res.listings = res.listings.map((it) => ({
			...it,
			listing: { ...(it?.listing || {}), _source: srcLabel },
		}));
		return res;
	};

	const fetchFromHub = async (hubUrl) => {
		try {
			const response = await reqInstance.get(`${hubUrl}/local/listings?${queryString}`);
			return response?.data || null;
		} catch (e) {
			log.debug('PropertyService -> loadProperty -> hub error:');
			log.debug(e);
			return null;
		}
	};

	// Try the origin hub first. If its copy is missing or not live (declined /
	// unlisted), ask the other hub once: its live copy wins, as in the merged
	// search. A dead copy is still shown when neither hub has a live one.
	let res = await fetchFromHub(primaryHub);
	if (isLiveHubCopy(res)) {
		res = stampSource(res, primaryHub);
	} else {
		const fallbackRes = await fetchFromHub(fallbackHub);
		if (isLiveHubCopy(fallbackRes)) {
			res = stampSource(fallbackRes, fallbackHub);
		} else if (res?.listings?.length) {
			res = stampSource(res, primaryHub);
		} else if (fallbackRes?.listings?.length) {
			res = stampSource(fallbackRes, fallbackHub);
		} else {
			res = res || fallbackRes || { listings: [] };
		}
	}
	dlog('loaded prop:', res?.listings?.[0]);
	return res;
	// localStorage.setItem('count", default_res.count);
	// return default_res;
};

export const loadlocationsbycountry = async (country) => {
	const tokenShub = constants.SHUB_STATIC_TOKEN;

	const reqInstance = axios.create({
		headers: {
			Authorization: `Bearer ${tokenShub}`
		}
	});
	const shublocationSearch = constants.SHUB_URL + `/local/listing-locations/${country}`;
	try {
		const response = await reqInstance.get(shublocationSearch);
		dlog('ppv - location call', response.data);
		if (response.data.status) {
			return response.data.locations;
		} else {
			return [];
		}
	} catch (error) {
		console.error('Error loading locations:', error);
		return [];
	}
	
}

export const loadlistings = async (location) => {
	const tokenShub = constants.SHUB_STATIC_TOKEN;

	const reqInstance = axios.create({
		headers: {
			Authorization: `Bearer ${tokenShub}`
		}
	});

	const shublistingSearch = constants.SHUB_URL + `/local/load-listingsbylocation?region=${location}`;
	try {
		const response = await reqInstance.get(shublistingSearch);
		dlog('ppv - listing call', response.data);
		if (response.data.status) {
			return response.data.listings;
		} else {
			return [];
		}
	} catch (error) {
		console.error('Error loading locations:', error);
		return [];
	}
}

export const loadAllActiveCountries = async() => {
	const tokenShub = constants.SHUB_STATIC_TOKEN;

	const reqInstance = axios.create({
		headers: {
			Authorization: `Bearer ${tokenShub}`
		}
	});
	const url = constants.SHUB_URL + '/local/get-all-countries';
	return reqInstance.get(`${url}`).then(async response => {
		const res = response.data;
		return res;
	})
	.catch(response => {
		console.error("PropertyService -> loadAllActiveCountries -> error: ", response);
		return response;
	});
}

// Admin: listings count grouped by country + destination (VTHUB).
export const loadListingCounts = async () => {
	const tokenShub = constants.SHUB_STATIC_TOKEN;

	const reqInstance = axios.create({
		headers: {
			Authorization: `Bearer ${tokenShub}`
		}
	});
	const url = constants.SHUB_URL + '/local/listing-counts';
	return reqInstance.get(`${url}`).then(response => response.data)
	.catch(error => {
		console.error("PropertyService -> loadListingCounts -> error: ", error);
		return { success: false, byCountry: [], byRegion: [] };
	});
}

// A villa's calendar: asked for early, with a time limit and a second try. See ./fullCalendar.js.
export { loadfullcalendar, loadfullcalendarAnswer, prefetchFullCalendar } from "./fullCalendar";

export const loadProperties = async (pageNumber, advancedFilters = null, options = {}) => {
	dlog('[loadProperties] advancedFilters:', advancedFilters, 'localStorage fallback:', {
		selectedPrices:    localStorage.getItem('selectedPrices'),
		selectedTypes:     localStorage.getItem('selectedTypes'),
		selectedMusthave:  localStorage.getItem('selectedMusthave'),
		selectedAmenities: localStorage.getItem('selectedAmenities'),
	});
	const tokenShub =constants.SHUB_STATIC_TOKEN;

	const reqInstance = axios.create({
		headers: {
			Authorization: `Bearer ${tokenShub}`
		}
	});
	/* filters on search 
		1. basic: 		destination, sortBy ( q, sort)
		  2. main page: 	available ,collections ,  onDemand  (avail, tags, tags)
		3. advanced: 	priceRange, propertyType, amenities, musthaves (tags, f , f, f)
	
		basic:
	
	*/
	const destination = getStorageValue("destination")
	// Always a sort: the agent's choice, else the shared default (price Low to High).
	// With none sent, the hub fell back to its own default — price High to Low.
	const sortBy = currentSortBy();
	const adults = parseInt(localStorage.getItem("adults") ? localStorage.getItem("adults") : 1);
	const children = parseInt(localStorage.getItem("children") ? localStorage.getItem("children") : 0);
	const guests = parseInt(adults + children) ? parseInt(adults + children) : 1;
	const bedrooms = parseInt(localStorage.getItem("bedrooms") ? localStorage.getItem("bedrooms") : 1);
	const bathrooms = parseInt(localStorage.getItem("bathrooms") ? localStorage.getItem("bathrooms") : 1);

	// --------

	//main page: available, onDemand, collections (onDemand + collections are by TAGS!!)
	// Guard against cleared/garbage values ("null", "undefined", "Invalid date", "") —
	// only format genuinely valid dates, else 0 so no `available` param is sent and the
	// backend doesn't choke on an "Invalid time value". Non-strict moment() still accepts
	// real dates in any reasonable format while rejecting the garbage strings.
	const dateFrom = moment(localStorage.getItem("dateFrom")).isValid() ? moment(localStorage.getItem("dateFrom")).format("YYYY-MM-DD") : 0;
	const dateTo = moment(localStorage.getItem("dateTo")).isValid() ? moment(localStorage.getItem("dateTo")).format("YYYY-MM-DD") : 0;
	const { flexDays: _flexDays, stayNights: _stayNights } = readFlexInputs();
	const available = buildAvailableParam(dateFrom, dateTo, guests, _flexDays, _stayNights);
	// tags:
	const collections = localStorage.getItem("collections") ? JSON.parse(localStorage.getItem("collections")) : null;
	// On-demand visibility: INCLUDE (default) vs EXCLUDE — never "only on-demand".
	// A selected on-demand toggle must not hide all priced inventory. hideOnDemand=true
	// drops on-demand listings via $nin; unset/false keeps them (the sort ranks them last).
	const hideOnDemand = localStorage.getItem("hideOnDemand") === 'true';
	// advanced: propertyType, amenities + musthaves

	const priceRanges   = advancedFilters?.priceRanges   ?? safeParseArray(localStorage.getItem("selectedPrices"));
	const propertyTypes = advancedFilters?.propertyTypes ?? safeParseArray(localStorage.getItem("selectedTypes"));
	const mustHaves     = advancedFilters?.mustHaves     ?? safeParseArray(localStorage.getItem("selectedMusthave"));
	const amenities     = advancedFilters?.amenities     ?? safeParseArray(localStorage.getItem("selectedAmenities"));
	const geo = localStorage.getItem("geo") ? localStorage.getItem("geo")  : '';
	//dlog("collections:",collections)
	// creating the filters and tags:
	let filters = []
	let tags = []
	let NOTtags = []

	// filters.push('{"field":"channel", "operator":"$in", "value": ["VT"]}') // filtering only VT channel

	if (collections) {
		// Every spelling of each chosen collection (Util/collections.js).
		tags = expandCollectionTags(collections);
	}
	if (hideOnDemand) { NOTtags.push('onDemand') }

	if (guests>=2) filters.push('{"field":"accommodates", "operator":"$gte", "value": [' + guests + ']}') // push bedrooms to filters
	if (bedrooms) {
		filters.push('{"field":"bedrooms", "operator":"$gte", "value": [' + bedrooms + ']}') // push bedrooms to filters
	} // adding bedrooms
	if (bathrooms) {
		filters.push('{"field":"bathrooms", "operator":"$gte", "value": [' + bathrooms + ']}') // push bathrooms to filters
	} // adding bathrooms

	if (tags.length > 0) {
		var tagsStr = tags.map((item) => item).join('","') // creates "tag1","tag2","tag3"...
		//dlog("tags for search:", tagsStr)
		filters.push('{"field":"tags", "operator":"$in", "value": ["' + tagsStr + '"]}') // push tags to filters
	}
	if (NOTtags.length > 0) {
		var NOTtagsStr = NOTtags.map((item) => item).join('","') // creates "tag1","tag2","tag3"...
		dlog("NOT tags for search:", tagsStr)
		filters.push('{"field":"!tags", "operator":"$nin", "value": ["' + NOTtagsStr + '"]}') // push tags to filters
	}

	if (propertyTypes && propertyTypes.length > 0) {
		const typeValuesStr = propertyTypes.map((item) => item.value).join('","');
		filters.push('{"field":"propertyType", "operator":"$in", "value": ["' + typeValuesStr + '"]}');
	}

	// Price range: with dates it is carried in `available` (priceMin/priceMax) and filtered
	// server-side on the card's stay total; without dates it travels as `budget` and is filtered
	// on the card's nightly price. See buildAvailableParam / buildBudgetParam.

	let amenitiesArr = []
	let mustHavesArr = []

	if (amenities.length > 0) {
		amenitiesArr = amenities.map((item) => item.value) // creates "pool","garden","musthave2"...
		// filters.push('{"field":"amenities", "operator":"$in", "value": ["' + amenitiesMustHavesStr + '"]}');
		//dlog("amenities:",amenitiesArr)
	}

	if (mustHaves.length > 0) {
		mustHavesArr = mustHaves.map((item) => item.value) // creates "pool","garden","musthave2"...
		// filters.push('{"field":"amenities", "operator":"$in", "value": ["' + amenitiesMustHavesStr + '"]}');
		//dlog("musthaves:",mustHavesArr)
	}

	// MustHaves + Amenities are ONE pool matched with $in — a listing qualifies if
	// it has ANY of the picked tags (same as the toggleOnDemand variant below).
	// Requiring ALL ($all on "mustHaves") was fatal: amenity strings come raw from
	// the PMSs, so combos like Beach Front+Pool+Sea View match 0 listings hub-wide,
	// and SHub's controller has no "mustHaves" field at all — its half of the merged
	// search errored out whenever a musthave was selected.
	const amenMustArr = amenitiesArr.concat(mustHavesArr)
	if (amenMustArr.length > 0) {
		const amenStr = amenMustArr.join('","')
		filters.push('{"field":"amenities", "operator":"$in", "value": ["' + amenStr + '"]}')
	}

	// "Exact filter matches only" header toggle — only bites when amenity/musthave
	// tags are actually picked (the other filters are already exact server-side).
	const strictMode = strictFiltersOn() && amenMustArr.length > 0;

	var filterS = filters.map((item) => item).join(",")
	const searchFilters = '[' + filterS + ']';

	const LIMIT = 12;

	const params = {
		// noCals: 'yes', // this will load without the calendars and save time and data
		limit: 12,
		skip: pageNumber * 12,
		status: 'Approved'
		//notEX: put to get results without the external props from BP/RU
	};
	if (destination && destination !== '') { params.q = destination } // adding destination to query
	if (geo && geo !== '') { params.geo = geo } // adding destination to query
	if (filters.length > 0) { params.filters = searchFilters } // adding filters
	if (sortBy !== '') { params.sortBy = sortBy } // adding sort by
	if ((dateFrom && dateTo)) { params.available = available } // adding available
	else { const budget = buildBudgetParam(); if (budget) params.budget = budget; } // no dates: the price range on its own
	// "Instant Book only" (results-header toggle, mirrors smilinghouse.com's
	// ?instant=1). A top-level hub param, NOT a `filters` field: the hub resolves
	// the EFFECTIVE mode (listing override ?? partner default) server-side, so the
	// filter applies before pagination and allCount stays exact. Only sent when ON
	// — a hub without the param handler would match it as a Mongo field and return
	// nothing, so an OFF toggle must not touch the wire query.
	if (instantOnlyOn()) { params.instantBook = '1' }
	if (geo) {
		delete params.q
		params.geo=geo
	}
	// Viewport map search ("search this area"): bounds = "neLat,neLng,swLat,swLng"
	// and a higher limit so the visible map shows more than one page of pins.
	// The bounds ARE the location filter, so drop any stale destination/geo
	// (e.g. a previous "city:Ibiza") — otherwise it AND-constrains the viewport
	// to that city and returns nothing when you pan elsewhere.
	if (options.bounds) {
		params.bounds = options.bounds;
		params.skip = 0;
		delete params.geo;
		delete params.q;
	}
	if (options.limit) { params.limit = options.limit; }
	// Map markers don't need calendars — ask the BE for the slim (no-calendar)
	// shape so a 100-200 listing viewport doesn't ship that many full calendars.
	if (options.noCals) { params.noCals = options.noCals; }

	// pointsOnly: lightweight map mode. Reuse all the filter/bounds/availability
	// params built above but ask the BE to return EVERY matching listing as a
	// point ({id,lat,lng,price,title,thumb}) with no page cap. Bypass the
	// listings cache/sequence machinery (the action layer guards latest-wins).
	if (options.pointsOnly) {
		params.pointsOnly = 'yes';
		delete params.limit; // BE returns the full set for points
		const pq = Object.keys(params).map(key => `${key}=${encodeURIComponent(params[key])}`).join('&');
		// The hub merges SHUB+VTHUB markers itself and dedupes on the base id.
		// Doing it here used to mean two requests and a title+coords key, which
		// double-pinned any villa whose two hubs held different titles.
		const url = constants.MERGE_SHUB_MAP
			? `${constants.SHUB_URL}/local/listings-search-merged?${pq}`
			: `${constants.SHUB_URL}/local/listings-search?${pq}`;
		try {
			const resp = await reqInstance.get(url);
			const data = resp?.data || {};
			return { points: Array.isArray(data.points) ? data.points : [], allCount: data.allCount || 0 };
		} catch (e) {
			// Never leave the map blank: fall back to this hub's own markers.
			console.error('[map] merged points failed, VTHUB-only:', e?.message);
			const resp = await reqInstance.get(`${constants.SHUB_URL}/local/listings-search?${pq}`);
			const data = resp?.data || {};
			return { points: Array.isArray(data.points) ? data.points : [], allCount: data.allCount || 0 };
		}
	}

	// Normal paginated search → merge VTHUB + SHUB (windowed, deduped, FE-cached).
	// The map viewport (bounds) + pointsOnly modes stay VTHUB-only. Kill-switch:
	// constants.MERGE_SHUB_SOURCE. SHUB failing degrades to VTHUB inside the merge.
	// Wrapped in executeSearch so the zero-result fallback below can re-run the
	// same search with relaxed filters through whichever path applies.
	const executeSearch = (searchParams) => {
		if (constants.MERGE_SHUB_SOURCE && !options.skipCache) {
			// The hub merges SHUB+VTHUB itself, so allCount is EXACT and the dedupe
			// covers the whole catalogue — the old in-browser merge could only count
			// and dedupe the rows this one browser had already downloaded.
			// On failure fall through to the single-hub search: degraded (VTHUB only)
			// but never empty.
			return loadServerMergedSearch(searchParams, reqInstance, options.sourceFilter || 'all', strictMode)
				.catch((err) => {
					console.error('[merge] server merge failed, falling back to VTHUB-only search:', err?.message);
					return directSearch(searchParams);
				});
		}
		return directSearch(searchParams);
	};

	const directSearch = (searchParams) => {
	const queryString = Object.keys(searchParams).map(key => `${key}=${encodeURIComponent(searchParams[key])}`).join('&');
	const shubSearch = constants.SHUB_URL + '/local/listings-search?';
	// Strict filtering happens client-side, so the wire query is identical either
	// way — key the cache on the mode too or the filtered result leaks across.
	const cacheKey = queryString + (strictMode ? '|strict' : '');

	const now = Date.now();
	evictExpired(now);
	// The side-list bounds search is now cacheable: the action layer snaps the
	// bounds to a coarse grid, so revisiting a region reuses the same queryString
	// and serves from cache (no network). A hit is the correct data for that
	// snapped cell; misses still go through the sequence/abort guard below.
	// (pointsOnly returns earlier and never reaches this cache.)
	const cached = options.skipCache ? undefined : searchCache.get(cacheKey);
	const cachedHasResults = (cached?.data?.count ?? cached?.data?.listings?.length ?? 0) > 0;
	if (cached && cached.expiresAt > now && cachedHasResults) {
		// Re-mirror localStorage counts so dependent UI matches the cached page.
		localStorage.setItem("count", cached.data.count);
		localStorage.setItem("wishlist", false);
		localStorage.setItem("withondemandpropertycount", cached.data.onDemandCount || 0);
		localStorage.setItem("allpropertycount", cached.data.allCount || 0);
		return cloneSearchResult(cached.data);
	} else if (cached && !cachedHasResults) {
		// Stale zero-result cache entry — drop and re-fetch.
		searchCache.delete(cacheKey);
	}

	const mySeq = ++latestSearchSequence;
	if (currentSearchAbortController) {
		currentSearchAbortController.abort();
	}
	currentSearchAbortController = new AbortController();
	const signal = currentSearchAbortController.signal;

	return reqInstance.get(`${shubSearch}${queryString}`, { signal })
		.then(async response => {
			const res = response.data;

			// Honor the latest-request guard before touching the payload.
			if (mySeq !== latestSearchSequence) {
				throw new SearchSupersededError();
			}

			// BE error / no listings (e.g. { success: false }, HTTP 200): return a
			// clean empty result so the thunk completes and the spinner stops,
			// instead of throwing on res.listings.sort and hanging the UI.
			if (!res || res.success === false || !Array.isArray(res.listings)) {
				console.error('Search returned no listings / error:', res?.error || res);
				localStorage.setItem("wishlist", true);
				return cloneSearchResult({ listings: [], count: 0, allCount: 0, onDemandCount: 0 });
			}

			// Merge the SHub bounds side-list so the map's property panel includes SHub
			// homes too (the markers already merge above). Gated on MERGE_SHUB_MAP — needs
			// SHub's bounds port live, else an un-ported SHub ignores `bounds` and returns
			// whole-world listings. Fail-soft: any error degrades to VTHub-only. mergeDedupeKey
			// collapses the same physical villa across hubs (BP by title+city, else base-id).
			if (constants.MERGE_SHUB_MAP && options.bounds) {
				try {
					const shRes = await reqInstance.get(`${constants.SHUB_SOURCE_URL}/local/listings-search?${queryString}`, { signal });
					if (mySeq !== latestSearchSequence) throw new SearchSupersededError();
					const shList = Array.isArray(shRes?.data?.listings) ? shRes.data.listings : [];
					if (shList.length) {
						const seen = new Set(res.listings.map(mergeDedupeKey));
						for (const it of shList) {
							const k = mergeDedupeKey(it);
							// Stamp _source:'SHUB' like loadMergedSearch does, so a SHub listing
							// opened from the map's bounds panel reaches the detail page tagged —
							// otherwise its quote/calendar route to VTHub.
							if (!seen.has(k)) { seen.add(k); res.listings.push({ ...it, listing: { ...(it?.listing || {}), _source: 'SHUB' } }); }
						}
						res.count        = (res.count || 0)        + (shRes.data.count || 0);
						res.allCount     = (res.allCount || 0)     + (shRes.data.allCount || 0);
						res.onDemandCount = (res.onDemandCount || 0) + (shRes.data.onDemandCount || 0);
					}
				} catch (e) {
					if (e instanceof SearchSupersededError) throw e;
					if (!signal.aborted) console.error('[map] SHub bounds merge failed (VTHub-only):', e?.message);
				}
			}

			// Stay-duration filter (terms.minNights / terms.maxNights) is now enforced
			// server-side in parseAvailableParam, so the client no longer drops listings here.

			// Best-effort tag ranking (see helpers above): stamp match counts and
			// bubble the closest matches up, before the onDemand-last pass.
			attachTagMatches(res.listings, tagMatchersFromFilters(searchParams.filters));
			rankByTagMatches(res.listings);

			// Strict mode: keep full matches only. Counts are BE-side (ANY-match),
			// so deduct what this page dropped — approximate beyond the current
			// page, but this path only serves bounds/no-merge searches.
			if (strictMode) {
				const before = res.listings.length;
				res.listings = res.listings.filter((it) =>
					(it?.listing?._tagMatch?.count || 0) >= (it?.listing?._tagMatch?.total || 0));
				const removed = before - res.listings.length;
				if (removed > 0) {
					if (typeof res.count === 'number') res.count = Math.max(0, res.count - removed);
					if (typeof res.allCount === 'number') res.allCount = Math.max(0, res.allCount - removed);
				}
			}

			// Sort and further process the listings
			res.listings.sort((a, b) => {
				const aOnDemand = a.listing.tags && a.listing.tags.includes("onDemand");
				const bOnDemand = b.listing.tags && b.listing.tags.includes("onDemand");

				if (aOnDemand && !bOnDemand) {
					return 1;
				} else if (!aOnDemand && bOnDemand) {
					return -1;
				} else {
					return 0;
				}
			});

			const hasResults = (res?.count ?? res?.listings?.length ?? 0) > 0;

			if (hasResults) {
				localStorage.setItem("count", res?.count ? res?.count : 0);
				localStorage.setItem("wishlist", false);
				localStorage.setItem("withondemandpropertycount", res?.onDemandCount ? res?.onDemandCount : 0);
				localStorage.setItem("allpropertycount", res?.allCount ? res?.allCount : 0);

				// Only cache fruitful searches. Zero-result responses skip the
				// cache so a re-search (after the user broadens filters or
				// inventory changes) always hits the BE and returns fresh data.
				// Viewport (bounds) searches are not cached (see skipCache note above).
				if (!options.skipCache) {
					searchCache.set(cacheKey, { data: res, expiresAt: Date.now() + SEARCH_CACHE_TTL_MS });
					evictExpired(Date.now());
				}
			} else {
				localStorage.setItem("wishlist", true);
			}

			return cloneSearchResult(res);
		})
		.catch(error => {
			if (
				signal.aborted ||
				axios.isCancel?.(error) ||
				error?.name === 'CanceledError' ||
				error?.name === 'AbortError' ||
				error?.code === 'ERR_CANCELED' ||
				error instanceof SearchSupersededError
			) {
				throw new SearchSupersededError();
			}
			throw error;
		});
	};

	const result = await executeSearch(params);

	// Zero-result rescue: instead of a dead "nothing found", progressively drop the
	// advanced filters — amenity/musthave tags first, then property type, then price
	// (destination/dates/guests always kept) — and return the first stage that has
	// results, flagged with `relaxedFilters` (the dropped labels) so the UI can say
	// "no exact matches for X — showing properties without these filters".
	// Skipped for map-viewport searches, which have their own empty-state copy.
	const resultCount = result?.count ?? result?.listings?.length ?? 0;
	if (resultCount === 0 && !options.bounds && !strictMode) { // strict = exact matches or nothing, never relax-rescue
		const itemLabels = (arr) => (arr || []).map((i) => i?.label ?? i?.value ?? String(i)).filter(Boolean);
		const relaxStages = [
			{ field: 'amenities',        labels: itemLabels(amenities.concat(mustHaves)) },
			{ field: 'propertyType',     labels: itemLabels(propertyTypes) },
			{ field: 'prices.basePrice', labels: ['price range'] },
		].filter((s) => filters.some((f) => f.indexOf('"field":"' + s.field + '"') !== -1));

		const droppedFields = [];
		const droppedLabels = [];
		try {
			for (const stage of relaxStages) {
				droppedFields.push(stage.field);
				droppedLabels.push(...stage.labels);
				const remaining = filters.filter((f) => !droppedFields.some((df) => f.indexOf('"field":"' + df + '"') !== -1));
				const relaxedParams = { ...params };
				if (remaining.length > 0) relaxedParams.filters = '[' + remaining.join(',') + ']';
				else delete relaxedParams.filters;
				const relaxed = await executeSearch(relaxedParams);
				if ((relaxed?.count ?? relaxed?.listings?.length ?? 0) > 0) {
					relaxed.relaxedFilters = droppedLabels;
					// Relaxed params carry no amenities filter, so the search itself
					// couldn't stamp match counts — rank against the ORIGINAL picks
					// so partial matches still float up and badge correctly.
					attachTagMatches(relaxed.listings, buildTagMatchers(amenMustArr));
					rankByTagMatches(relaxed.listings);
					return relaxed;
				}
			}
		} catch (e) {
			if (e instanceof SearchSupersededError || e?.name === 'SearchSupersededError') throw e;
			console.error('[search] relaxed-filters fallback failed:', e?.message);
		}
	}
	return result;
};

export const toggleOnDemandandloadProperties = async (pageNumber, tmpondemand, advancedFilters = null) => {
	//log.debug("PropertyService -> loadProperties -> Enter");

	const tokenShub = constants.SHUB_STATIC_TOKEN;

	const reqInstance = axios.create({
		headers: {
			Authorization: `Bearer ${tokenShub}`
		}
	});
	/* filters on search 
		1. basic: 		destination, sortBy ( q, sort)
		  2. main page: 	available ,collections ,  onDemand  (avail, tags, tags)
		3. advanced: 	priceRange, propertyType, amenities, musthaves (tags, f , f, f)
	
		basic:
	
	*/
	const destination = getStorageValue("destination")
	// Always a sort: the agent's choice, else the shared default (price Low to High).
	// With none sent, the hub fell back to its own default — price High to Low.
	const sortBy = currentSortBy();
	const adults = parseInt(localStorage.getItem("adults") ? localStorage.getItem("adults") : 0);
	const children = parseInt(localStorage.getItem("children") ? localStorage.getItem("children") : 0);
	const guests = parseInt(adults + children) ? parseInt(adults + children) : 1;
	const bedrooms = parseInt(localStorage.getItem("bedrooms") ? localStorage.getItem("bedrooms") : 0);
	const bathrooms = parseInt(localStorage.getItem("bathrooms") ? localStorage.getItem("bathrooms") : 0);

	// --------

	//main page: available, onDemand, collections (onDemand + collections are by TAGS!!)
	// Guard against cleared/garbage values ("null", "undefined", "Invalid date", "") —
	// only format genuinely valid dates, else 0 so no `available` param is sent and the
	// backend doesn't choke on an "Invalid time value". Non-strict moment() still accepts
	// real dates in any reasonable format while rejecting the garbage strings.
	const dateFrom = moment(localStorage.getItem("dateFrom")).isValid() ? moment(localStorage.getItem("dateFrom")).format("YYYY-MM-DD") : 0;
	const dateTo = moment(localStorage.getItem("dateTo")).isValid() ? moment(localStorage.getItem("dateTo")).format("YYYY-MM-DD") : 0;
	const { flexDays: _flexDays, stayNights: _stayNights } = readFlexInputs();
	const available = buildAvailableParam(dateFrom, dateTo, guests, _flexDays, _stayNights);
	// tags:
	const collections = localStorage.getItem("collections") ? JSON.parse(localStorage.getItem("collections")) : null;
	// tmpondemand carries the toggled "hide on-demand" flag from the action.
	const hideOnDemand = tmpondemand
	// advanced: propertyType, amenities + musthaves

	const priceRanges   = advancedFilters?.priceRanges   ?? safeParseArray(localStorage.getItem("selectedPrices"));
	const propertyTypes = advancedFilters?.propertyTypes ?? safeParseArray(localStorage.getItem("selectedTypes"));
	const mustHaves     = advancedFilters?.mustHaves     ?? safeParseArray(localStorage.getItem("selectedMusthave"));
	const amenities     = advancedFilters?.amenities     ?? safeParseArray(localStorage.getItem("selectedAmenities"));
	const geo = localStorage.getItem("geo") ? localStorage.getItem("geo")  : '';
	//dlog("collections:",collections)
	// creating the filters and tags:
	let filters = []
	let tags = []
	let NOTtags = []

	// filters.push('{"field":"channel", "operator":"$in", "value": ["VT"]}') // filtering only VT channel

	if (collections) {
		// Every spelling of each chosen collection (Util/collections.js).
		tags = expandCollectionTags(collections);
	}
	if (hideOnDemand) { NOTtags.push('onDemand') }

	dlog("guests:",guests)
	if (guests>2) filters.push('{"field":"accommodates", "operator":"$gte", "value": [' + guests + ']}') // push bedrooms to filters
	if (bedrooms) {
		filters.push('{"field":"bedrooms", "operator":"$gte", "value": [' + bedrooms + ']}') // push bedrooms to filters
	} // adding bedrooms
	if (bathrooms) {
		filters.push('{"field":"bathrooms", "operator":"$gte", "value": [' + bathrooms + ']}') // push bathrooms to filters
	} // adding bathrooms

	if (tags.length > 0) {
		var tagsStr = tags.map((item) => item).join('","') // creates "tag1","tag2","tag3"...
		//dlog("tags for search:", tagsStr)
		filters.push('{"field":"tags", "operator":"$in", "value": ["' + tagsStr + '"]}') // push tags to filters
	}
	if (NOTtags.length > 0) {
		var NOTtagsStr = NOTtags.map((item) => item).join('","') // creates "tag1","tag2","tag3"...
		dlog("NOT tags for search:", tagsStr)
		filters.push('{"field":"!tags", "operator":"$nin", "value": ["' + NOTtagsStr + '"]}') // push tags to filters
	}

	if (propertyTypes && propertyTypes.length > 0) {
		const typeValuesStr = propertyTypes.map((item) => item.value).join('","');
		filters.push('{"field":"propertyType", "operator":"$in", "value": ["' + typeValuesStr + '"]}');
	}

	// Price range: with dates it is carried in `available` (priceMin/priceMax) and filtered
	// server-side on the card's stay total; without dates it travels as `budget` and is filtered
	// on the card's nightly price. See buildAvailableParam / buildBudgetParam.

	let amenitiesArr = []
	let mustHavesArr = []

	if (amenities.length > 0) {
		amenitiesArr = amenities.map((item) => item.value) // creates "pool","garden","musthave2"...
		// filters.push('{"field":"amenities", "operator":"$in", "value": ["' + amenitiesMustHavesStr + '"]}');
		//dlog("amenities:",amenitiesArr)
	}

	if (mustHaves.length > 0) {
		mustHavesArr = mustHaves.map((item) => item.value) // creates "pool","garden","musthave2"...
		// filters.push('{"field":"amenities", "operator":"$in", "value": ["' + amenitiesMustHavesStr + '"]}');
		//dlog("musthaves:",mustHavesArr)
	}

	const amenMust = amenitiesArr.concat(mustHavesArr) // in shub amenities and musthave are the same!

	if (amenMust.length > 0) {
		var amenStr = amenMust.map((item) => item).join('","') // creates "tag1","tag2","tag3"...
		//dlog("amen:",amenStr)
		filters.push('{"field":"amenities", "operator":"$in", "value": ["' + amenStr + '"]}') // push amenities to filters
	}

	var filterS = filters.map((item) => item).join(",")
	const searchFilters = '[' + filterS + ']';

	const params = {
				//noCals: 'yes', // this will load without the calendars and save time and data
		limit: 12,
		skip: pageNumber * 12,
		//notEX: put to get results without the external props from BP/RU
	};
	if (destination && destination !== '') { params.q = destination } // adding destination to query
	if (geo && geo !== '') { params.geo = geo } // adding destination to query
	if (filters.length > 0) { params.filters = searchFilters } // adding filters
	if (sortBy !== '') { params.sortBy = sortBy } // adding sort by
	if ((dateFrom && dateTo)) { params.available = available } // adding available
	else { const budget = buildBudgetParam(); if (budget) params.budget = budget; } // no dates: the price range on its own
	// "Instant Book only" (results-header toggle, mirrors smilinghouse.com's
	// ?instant=1). A top-level hub param, NOT a `filters` field: the hub resolves
	// the EFFECTIVE mode (listing override ?? partner default) server-side, so the
	// filter applies before pagination and allCount stays exact. Only sent when ON
	// — a hub without the param handler would match it as a Mongo field and return
	// nothing, so an OFF toggle must not touch the wire query.
	if (instantOnlyOn()) { params.instantBook = '1' }
	if (geo) {
		delete params.q
		params.geo=geo
	}
	dlog("search params:", params)

	const queryString = Object.keys(params).map(key => `${key}=${params[key]}`).join('&');
	const shubSearch = constants.SHUB_URL + '/local/listings?';

	return reqInstance.get(`${shubSearch}${queryString}`)
		.then(async response => {
			const res = response.data;
			dlog(res)
			if (res.count) {
				localStorage.setItem("allpropertycount", res?.count ? res?.count : 0);
			}
			// Same best-effort tag ranking as the main search path.
			if (Array.isArray(res?.listings)) {
				attachTagMatches(res.listings, tagMatchersFromFilters(params.filters));
				rankByTagMatches(res.listings);
				// Same strict semantics as the main path (full matches only).
				if (strictFiltersOn() && amenMust.length > 0) {
					res.listings = res.listings.filter((it) =>
						(it?.listing?._tagMatch?.count || 0) >= (it?.listing?._tagMatch?.total || 0));
				}
			}
			return res;
		})
		.catch(response => {
			console.error("PropertyService -> loadPropertiestoggleondemand -> error: ", response);
			return response;
		});
};

export const loadreservationreport = async (data) => {
	const userToken = localStorage.getItem('jToken');
	const userRequest = axios.create({
		baseURL: `${constants.BASE_URL}/reservation/get-reservation-reports?agent_id=${data.agent_id}&agency_id=${data.agency_id}`,
		headers: {
			token: `Bearer ${userToken}`,
		},
	});

	return await userRequest.get()
}

// Build the same VTHUB search filters the main search uses, from localStorage,
// for filtering the FAVORITES list. Returns the filters[] (JSON strings) plus the
// q/geo/sortBy/available params. (Kept separate from loadProperties so the main
// search is never touched.)
export const buildFavoriteSearchParams = () => {
	const destination = getStorageValue("destination");
	const sortBy = localStorage.getItem("sortBy") || '';
	const adults = parseInt(localStorage.getItem("adults") || 1);
	const children = parseInt(localStorage.getItem("children") || 0);
	const guests = parseInt(adults + children) || 1;
	const bedrooms = parseInt(localStorage.getItem("bedrooms") || 1);
	const bathrooms = parseInt(localStorage.getItem("bathrooms") || 1);
	// Guard against cleared/garbage values ("null", "undefined", "Invalid date", "") —
	// only format genuinely valid dates, else 0 so no `available` param is sent and the
	// backend doesn't choke on an "Invalid time value". Non-strict moment() still accepts
	// real dates in any reasonable format while rejecting the garbage strings.
	const dateFrom = moment(localStorage.getItem("dateFrom")).isValid() ? moment(localStorage.getItem("dateFrom")).format("YYYY-MM-DD") : 0;
	const dateTo = moment(localStorage.getItem("dateTo")).isValid() ? moment(localStorage.getItem("dateTo")).format("YYYY-MM-DD") : 0;
	const { flexDays: _flexDays, stayNights: _stayNights } = readFlexInputs();
	// Favourites is narrowed by price only when the agent ticked a Budget bracket. With none
	// ticked nothing here can make a saved villa disappear.
	const agentChosePrice = !isPriceDefaulted();
	const available = buildAvailableParam(dateFrom, dateTo, guests, _flexDays, _stayNights, { usePrice: agentChosePrice });
	const collections = localStorage.getItem("collections") ? JSON.parse(localStorage.getItem("collections")) : null;
	const hideOnDemand = localStorage.getItem("hideOnDemand") === 'true';
	// Same rule for the per-night prices.basePrice filter below: only an agent's own price
	// narrows favourites (review round 1, 2026-09-24).
	// The ticked brackets' outer bounds, per night (this filter list has no "either range").
	const priceBounds = agentChosePrice ? priceRequestBounds(readStoredPriceItems()) : null;
	const propertyTypes = safeParseArray(localStorage.getItem("selectedTypes"));
	const mustHaves = safeParseArray(localStorage.getItem("selectedMusthave"));
	const amenities = safeParseArray(localStorage.getItem("selectedAmenities"));
	const geo = localStorage.getItem("geo") || '';

	const filters = [];
	let tags = [];
	const NOTtags = [];
	if (collections) tags = expandCollectionTags(collections);
	if (hideOnDemand) NOTtags.push('onDemand');
	if (guests >= 2) filters.push('{"field":"accommodates", "operator":"$gte", "value": [' + guests + ']}');
	if (bedrooms) filters.push('{"field":"bedrooms", "operator":"$gte", "value": [' + bedrooms + ']}');
	if (bathrooms) filters.push('{"field":"bathrooms", "operator":"$gte", "value": [' + bathrooms + ']}');
	if (tags.length) filters.push('{"field":"tags", "operator":"$in", "value": ["' + tags.join('","') + '"]}');
	if (NOTtags.length) filters.push('{"field":"!tags", "operator":"$nin", "value": ["' + NOTtags.join('","') + '"]}');
	if (propertyTypes && propertyTypes.length) filters.push('{"field":"propertyType", "operator":"$in", "value": ["' + propertyTypes.map((i) => i.value).join('","') + '"]}');
	if (priceBounds) {
		filters.push('{"field":"prices.basePrice", "operator":"$gte", "value": ' + priceBounds.priceMin + '}');
		if (priceBounds.priceMax != null) filters.push('{"field":"prices.basePrice", "operator":"$lte", "value": ' + priceBounds.priceMax + '}');
	}
	// Same ANY-match pool as the main search: musthaves + amenities in one $in.
	const favAmenMust = (amenities || []).concat(mustHaves || []).map((i) => i.value);
	if (favAmenMust.length) filters.push('{"field":"amenities", "operator":"$in", "value": ["' + favAmenMust.join('","') + '"]}');

	return { filters, destination, geo, sortBy, available };
};

export const loadFavorites = async (pageNumber, admin = false) => {
	log.debug("PropertyService -> loadFavorites -> Enter-**-*-*-*--*-*-*");
	const TOKEN = localStorage.getItem("jToken");

	const userRequest = axios.create({
		baseURL: constants.BASE_URL,
		headers: {
			token: `Bearer ${TOKEN}`,
		},
	});

	const token = constants.SHUB_STATIC_TOKEN;

	const reqInstance = axios.create({
		headers: {
			Authorization: `Bearer ${token}`
		}
	});

	const readFavorites = async () => {
		const agentID = localStorage.getItem("agent_id");
		let getFavUrl = admin ? `/favorite/get-favorites` : `/favorite/get-favorites?agent_id=${agentID}`;
		return userRequest.get(getFavUrl)
			.then(async response => {
				const favoriteIdsArray = response?.data[0]?.favorites?.map(item => item.favorite);
				localStorage.setItem('favoriteIds', JSON.stringify(favoriteIdsArray));
				const res = response.data[0];
				//dlog("PropertyService -> loadFavorties -> response: ", res);
				if (res.totalFavorites) {
					//dlog("count favorites:", res.totalFavorites);
					{ localStorage.setItem("totalFavorites", res.totalFavorites); }
				}

				const favorites = res.favorites;
				const sortedFavorites = (favorites || []).slice().sort((a, b) => (b.count || 0) - (a.count || 0));
				const LIMIT = 12;

				// Send ALL favorite IDs + the active search filters and let VTHUB filter
				// AND paginate within the favorites. The returned count is the FILTERED
				// total (used for pagination), not the raw favorite count.
				const allFavoriteIds = sortedFavorites.map(item => item.favorite);
				const favoritesCSV = allFavoriteIds.join('", "');
				const idsFilter = '{"field":"ids", "operator":"$in", "value": ["' + favoritesCSV + '"]}';
				const { filters: searchFilters, destination, geo, sortBy, available } = buildFavoriteSearchParams();
				const allFilters = [idsFilter, ...searchFilters];

				const params = {
					filters: '[' + allFilters.join(',') + ']',
					limit: LIMIT,
					skip: pageNumber * LIMIT,
					status: 'Approved',
				};
				if (destination && destination !== '') { params.q = destination; }
				if (geo && geo !== '') { params.geo = geo; delete params.q; }
				if (sortBy && sortBy !== '') { params.sortBy = sortBy; }
				if (available && available !== '') { params.available = available; }

				const queryString = Object.keys(params).map(key => `${key}=${encodeURIComponent(params[key])}`).join('&');
				const shubSearch = constants.SHUB_URL + '/local/listings-search?';
				return reqInstance.get(`${shubSearch}${queryString}`)
					.then(async response => {
						const res = response.data;
						// res.count is the filtered total across all pages.
						if (typeof res.count !== "undefined") {
							localStorage.setItem("count", res.count);
						}
						// attach favorite count from the FULL favorites map to matching listings
                        if (Array.isArray(res.listings)) {
                            const favCountMap = new Map(sortedFavorites.map(f => [f.favorite, f.count]));
                            res.listings = res.listings.map(listingObj => {
                                const listingId = listingObj?.listing?._id || listingObj?._id;
                                const favCount = favCountMap.get(listingId);
                                if (typeof favCount !== "undefined") {
                                    listingObj.favoriteCount = favCount;
                                    if (listingObj.listing) listingObj.listing.favoriteCount = favCount;
                                }
                                return listingObj;
                            });
                        }
						return res;
					})
					.catch(response => {
						log.debug("PropertyService -> loadFavorites -> error: ");
						log.debug(response);
						return response;
					})
			})
			.catch(response => {
				log.debug("PropertyService -> loadFavorites -> error: ");
				log.debug(response);
				return response;
			})

	}

	let favorites = readFavorites(); // reads the favorites of the agent from backend
	return favorites;

	// localStorage.setItem("count", default_res.count);
	// return default_res;
};

export const getQoute = async (propertyId, body) => {
	// log.debug("PropertyService -> loadFavorites -> Enter");

	if (!body || !propertyId) { return "no PID or body" }

	else {
		dlog("qoute=", propertyId, body);
		const token2 = constants.SHUB_STATIC_TOKEN;

		const reqInstance = axios.create({
			headers: {
				Authorization: `Bearer ${token2}`
			}
		});
		const qouteURL = constants.SHUB_URL+'/qoute/'+propertyId;
		return reqInstance.get(`${qouteURL}`, body)
			.then(async response => {
				const res = response.data;
				dlog("PropertyService -> getQoute -> response: ", res);
				if (res.qouteResult) {
				}
				return res;
			})
			.catch(response => {
				log.debug("PropertyService -> getQoute -> error: ");
				log.debug(response);
				return response;
			})
	}

};

// Unified live-quote client — ONE endpoint for EVERY channel. Calls
// GET <hub>/api/booking/quote on the listing's ORIGIN hub (srcHub 'sh' → SHUB
// source, else VTHUB) and returns the normalized NET DTO (or { ok:false, ... }).
// NET only — the caller applies markup (pricingPolicy.dividerFor); the endpoint
// never returns a marked-up price. Gated by constants.USE_UNIFIED_QUOTE: returns
// null when the flag is off or inputs are missing, so callers fall back to their
// existing per-channel path with zero behaviour change.
export const getUnifiedQuote = async (propertyId, { dateFrom, dateTo, guests, currency, srcHub } = {}) => {
	if (!constants.USE_UNIFIED_QUOTE) return null;
	if (!propertyId || !dateFrom || !dateTo) return null;
	const base = srcHub === 'sh' ? constants.SHUB_SOURCE_URL : constants.SHUB_URL;
	try {
		const resp = await axios.get(`${base}/api/booking/quote`, {
			params: {
				listingId: propertyId,
				checkIn: dateFrom,
				checkOut: dateTo,
				guests: guests || 1,
				currency: currency || 'USD',
			},
			// Bearer works on BOTH hubs (verifyAccessToken/KNOWN_TOKENS); x-api-key kept
			// for VTHUB parity. SHUB rejects VTHUB's x-api-key, so the Bearer is what carries it.
			headers: { 'x-api-key': constants.SHUB_API_KEY, Authorization: `Bearer ${constants.SHUB_STATIC_TOKEN}` },
		});
		return resp?.data || null;
	} catch (e) {
		// Never throw into the price path — degrade to unavailable/retryable so the
		// caller keeps its local-calendar fallback during rollout.
		dlog('getUnifiedQuote error:', e?.message);
		return { ok: false, available: false, error: e?.message || 'quote failed', retryable: true };
	}
};

// Channel create for ONE VT reservation — admin only. POSTs { reservationID } to
// VT-Backend /hub-booking/create. The SERVER builds the booking from the stored
// reservation (owning hub, server-side NET quote, its own Idempotency-Key), books it on
// the channel, stores the channel ids and approves the reservation. The browser sends
// no listing, dates, guest, price, hub or key any more.
// Resolves { ok, reservationId, confirmationCode, source, approved } on success.
// Throws on non-2xx (caller reads err.response): 504, or 409 code 'pending', means the
// outcome is UNKNOWN (never rebook); any other 409/422 is a definite no with a code.
// acceptLivePrice:true = the admin accepted the live channel price after a 409
// price_changed; VT-Backend then books at that NET (and records who accepted it).
export const createBooking = async (reservationID, { acceptLivePrice } = {}) => {
	const body = { reservationID };
	if (acceptLivePrice === true) body.acceptLivePrice = true;
	const resp = await axios.post(`${constants.BASE_URL}/hub-booking/create`, body,
		{ headers: { Authorization: `Bearer ${localStorage.getItem('jToken')}` } });
	return resp?.data || null;
};

// Unified BATCH live-quote client — one stay, many listings (the search page shape).
// POSTs to <hub>/api/booking/quote-batch on ONE origin hub (srcHub 'sh' → SHUB
// source, else VTHUB), so the caller must group listingIds by their _source first.
// The hub serves cache hits instantly, fetches misses under a short deadline, and
// returns anything slow as `pending` (it keeps warming the cache in the background,
// so a later tick finds it). Returns { ok, quotes:{[id]:NET_DTO}, pending:[id] } or
// null when the flag is off / inputs are missing — callers then keep stored prices.
// Never throws into the price path (mirrors getUnifiedQuote's degrade-not-throw).
export const getUnifiedQuoteBatch = async ({ listingIds, checkIn, checkOut, guests, currency, srcHub } = {}) => {
	if (!constants.USE_UNIFIED_QUOTE) return null;
	if (!Array.isArray(listingIds) || !listingIds.length || !checkIn || !checkOut) return null;
	const base = srcHub === 'sh' ? constants.SHUB_SOURCE_URL : constants.SHUB_URL;
	try {
		const resp = await axios.post(
			`${base}/api/booking/quote-batch`,
			{ listingIds, checkIn, checkOut, guests: guests || 1, currency: currency || 'USD' },
			{ headers: { 'x-api-key': constants.SHUB_API_KEY, Authorization: `Bearer ${constants.SHUB_STATIC_TOKEN}` } },
		);
		return resp?.data || null;
	} catch (e) {
		dlog('getUnifiedQuoteBatch error:', e?.message);
		return null; // degrade silently — the card keeps its stored (>0-guarded) price
	}
};

// Unified reservation-CANCEL client — the cancel counterpart to createBooking.
// POSTs { reservationId } (= the reservation's partnerReservationID) to VT-Backend
// /hub-booking/cancel, which finds the VT reservation, picks its owning hub and forwards
// to <hub>/api/booking/cancel (idempotent: a 2nd call → duplicate:true).
// srcHub / legacy:true are ONLY for an id VT-Backend may not know (a raw channel id with
// no VT reservation) — admin only; VT-Backend ignores legacy for anyone else. A known
// reservation's hub comes from the server's own record, so callers omit both.
// Throws on non-2xx so the caller can react.
// cancelledBy ('host' | 'guest'): VT-Backend declines and refunds the VT reservation itself
// once the channel cancel succeeds, under this cancellation-policy choice (VT-Backend #85).
export const cancelBooking = async (reservationId, { srcHub, legacy, cancelledBy } = {}) => {
	const body = { reservationId };
	if (srcHub) body.srcHub = srcHub;
	if (legacy === true) body.legacy = true;
	if (cancelledBy) body.cancelledBy = cancelledBy;
	const resp = await axios.post(`${constants.BASE_URL}/hub-booking/cancel`, body,
		{ headers: { Authorization: `Bearer ${localStorage.getItem('jToken')}` } });
	return resp?.data || null;
};

export const loadPropertiesIds = async (propIds) => {
	log.debug("PropertyService -> loadFavorites -> Enter-**-*-*-*--*-*-*");
	const TOKEN = localStorage.getItem("jToken");

	const userRequest = axios.create({
		baseURL: constants.BASE_URL,
		headers: {
			token: `Bearer ${TOKEN}`,
		},
	});

	const token = constants.SHUB_STATIC_TOKEN;

	const reqInstance = axios.create({
		headers: {
			Authorization: `Bearer ${token}`
		}
	});

	const readIds = async (ids) => {

		const propertiesCSV = ids.join('", "');
		dlog(propertiesCSV, "=ids");
		const filter_ids = '[{"field":"ids", "operator":"$in", "value": ["' + propertiesCSV + '"]}]';
		const params = {
			filters: filter_ids,
			limit: 50,
			//noCals: 'yes'
		};

		const queryString = Object.keys(params).map(key => key + '=' + params[key]).join('&');
		const shubSearch = constants.SHUB_URL + '/local/listings?';
		return reqInstance.get(`${shubSearch}${queryString}`)
			.then(async response => {
				const res = response.data;
				// dlog("PropertyService -> loadProperties -> response: <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<-----", res);
				if (res.count) {
					//dlog("count:", res.count);
					{ localStorage.setItem("totalSelectedPropertiesItem", res.count); }
				}
				return res;
			})
			.catch(response => {
				log.debug("PropertyService -> loadIds -> error: ");
				log.debug(response);
				return response;
			})
	}
	let properties = await readIds(propIds); // reads the favorites of the agent from backend
	dlog('loaded props:', properties.listings)
	localStorage.setItem('selectedPropertiesItem', JSON.stringify(properties.listings));
	return properties.listings;

	// localStorage.setItem("count", default_res.count);
	// return default_res;
};

// The group endpoint returns a NORMALIZED payload (clusters reference member
// ids; heavy property records ship once in propertiesById) so overlapping
// clusters don't multiply the payload. Hydrate it back into the legacy
// clusters[].properties shape here so ClusterCard/PropertyBox stay unchanged.
// Legacy (non-normalized) responses pass through untouched.
const hydrateNormalizedClusters = (data) => {
	if (!data?.normalized || !Array.isArray(data.clusters)) return data;
	const propertiesById = data.propertiesById || {};
	const clusters = data.clusters.map((cluster) => {
		const properties = (cluster.members || [])
			.map((member) => {
				const shared = propertiesById[member.id];
				if (!shared) return null;
				return {
					...shared,
					// Keep the VTHub listing id on the property: the verify flow and
					// the CLUSTER_QUOTES_VERIFIED reducer key quotes by it.
					id: member.id,
					// Per-cluster values live on the member entry, including the
					// pairwise distances the old shape kept in xdata.
					xdata: { ...shared.xdata, clusterDistances: member.clusterDistances || [] },
					quote: member.quote,
					pricedForGuests: member.pricedForGuests,
				};
			})
			.filter(Boolean);
		const { members, ...rest } = cluster;
		return { ...rest, properties };
	});
	return { ...data, clusters };
};

// Abort + sequence guard for cluster searches — mirrors the normal-search
// pattern above. A new call aborts the in-flight one; a response landing after
// a newer request started is reported as superseded so the thunk can ignore it
// silently (no error toast for self-inflicted cancellations).
let currentClusterAbortController = null;
let latestClusterSequence = 0;
const CLUSTER_REQUEST_TIMEOUT_MS = 90000;

export const loadClusters = async (groupSearch) => {
	try {
		const tokenShub = constants.SHUB_STATIC_TOKEN;

		const mySeq = ++latestClusterSequence;
		if (currentClusterAbortController) {
			currentClusterAbortController.abort();
		}
		currentClusterAbortController = new AbortController();
		const signal = currentClusterAbortController.signal;

		const reqInstance = axios.create({
			headers: {
				Authorization: `Bearer ${tokenShub}`
			},
			timeout: CLUSTER_REQUEST_TIMEOUT_MS
		});

		const dateFrom = groupSearch?.groupDateFrom
			? moment(groupSearch.groupDateFrom).format("YYYY-MM-DD")
			: '';
		const dateTo = groupSearch?.groupDateTo
			? moment(groupSearch.groupDateTo).format("YYYY-MM-DD")
			: '';

		let agentInfo = {};
		try {
			agentInfo = JSON.parse(localStorage.getItem("agent") || "{}") || {};
		} catch (_) {
			agentInfo = {};
		}
		const currency = agentInfo?.currency || "USD";

		const params = {};
		if (dateFrom) params.checkIn = dateFrom;
		if (dateTo) params.checkOut = dateTo;
		if (groupSearch?.totalGuests) params.totalGuests = groupSearch.totalGuests;
		params.currency = currency;
		params.limit = 20; // BE default; explicit so the contract is visible here
		// Price ordering is SERVER-side: the page is a slice of the full priced
		// set, so only the server can order it truthfully. Cheapest first unless the
		// agent chose otherwise (Util/clusterSort.js).
		params.sort = serverClusterSort(groupSearch?.clusterSort);
		// AUTH-05/06/07: this used to send agent_role because VTHub's group-search guard
		// read it from the query — the caller asserting its own role. The guard reads the
		// hub session now (VTHUB groupSearchGuard.js), which only a VT admin is given, so
		// there is nothing to send.
		if (groupSearch?.minBudget !== '' && groupSearch?.minBudget != null) params.minBudget = groupSearch.minBudget;
		if (groupSearch?.maxBudget !== '' && groupSearch?.maxBudget != null) params.maxBudget = groupSearch.maxBudget;
		if (groupSearch?.radiusKm) params.radiusKm = groupSearch.radiusKm;
		if (groupSearch?.maxProperties) params.maxProperties = groupSearch.maxProperties;

		const cities = (groupSearch?.groupDestinations || [])
			.filter(d => d?.type === 'city')
			.map(d => d.name);
		if (cities.length > 0) params.cities = JSON.stringify(cities);

		const firstRegion = (groupSearch?.groupDestinations || []).find(d => d?.type === 'region');
		if (firstRegion?.name) params.geo = `region:${firstRegion.name}`;

		// Map-move search: the viewport box replaces the destination scope
		// entirely (BE: bounds wins over geo/cities). Snapped to a 0.02° grid
		// so nearby pans hit the same BE result-cache entry.
		if (groupSearch?.boundsOverride) {
			const snap = (v) => (Math.round(v / 0.02) * 0.02).toFixed(2);
			const b = groupSearch.boundsOverride;
			params.bounds = [snap(b.south), snap(b.west), snap(b.north), snap(b.east)].join(',');
			delete params.cities;
			delete params.geo;
		}

		const queryString = Object.keys(params)
			.map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
			.join('&');
		const url = constants.SHUB_URL + '/local/group-accommodation-search?';
		dlog('group accommodation search params:', params);

		try {
			// Group search is served by VTHUB (constants.SHUB_URL) ONLY — the
			// /local/group-accommodation-search endpoint and its pricing engine are
			// deployed there. Do NOT fan this out to the second normal-search
			// backend (SHUB_SOURCE_URL / triangle.luxury): it is a different
			// system and must not receive group-search traffic.
			const response = await reqInstance.get(url + queryString, { signal });
			if (mySeq !== latestClusterSequence) {
				return { success: false, superseded: true, clusters: [] };
			}
			return hydrateNormalizedClusters(response.data);
		} catch (error) {
			const isAborted =
				signal.aborted ||
				axios.isCancel?.(error) ||
				error?.name === 'CanceledError' ||
				error?.name === 'AbortError' ||
				error?.code === 'ERR_CANCELED';
			if (isAborted || mySeq !== latestClusterSequence) {
				return { success: false, superseded: true, clusters: [] };
			}
			console.error("PropertyService -> loadClusters -> error: ", error);
			const isTimeout = error?.code === 'ECONNABORTED';
			const apiError = error?.response?.data;
			return {
				success: false,
				error: isTimeout
					? 'Group search timed out - try a smaller radius or fewer cities'
					: (apiError?.error || error?.message || 'Network error'),
				clusters: []
			};
		}
	} catch (outerError) {
		// Synchronous throw before the request (e.g. malformed state) must never
		// leave the spinner stuck — normalize to the standard failure shape.
		console.error("PropertyService -> loadClusters -> unexpected error: ", outerError);
		return {
			success: false,
			error: outerError?.message || 'Unexpected error',
			clusters: []
		};
	}
};

// Abort the in-flight cluster search (user pressed Cancel). Bumping the
// sequence marks any late response as superseded so it can't render.
export const cancelClusterSearch = () => {
	latestClusterSequence++;
	if (currentClusterAbortController) {
		currentClusterAbortController.abort();
		currentClusterAbortController = null;
	}
};

/**
 * Drill-in price verification for one cluster's members. Returns
 * { success, quotes: { [id]: quote|null } } — quotes carry source
 * (RU_API / SH_API_QUOTE / GUESTY_QUOTE / *_CALENDAR) for the verified badge.
 */
export const verifyClusterQuotesApi = async ({ items, checkIn, checkOut }) => {
	try {
		const tokenShub = constants.SHUB_STATIC_TOKEN;

		let agentInfo = {};
		try {
			agentInfo = JSON.parse(localStorage.getItem("agent") || "{}") || {};
		} catch (_) { agentInfo = {}; }

		const params = {
			items: JSON.stringify(items),
			checkIn: checkIn ? moment(checkIn).format("YYYY-MM-DD") : '',
			checkOut: checkOut ? moment(checkOut).format("YYYY-MM-DD") : '',
			currency: agentInfo?.currency || 'USD'
		};
		// AUTH-05/06/07: agent_role went here too, for the same guard. It reads the hub
		// session now, so the role is no longer the caller's to assert.

		const queryString = Object.keys(params)
			.map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
			.join('&');

		const reqInstance = axios.create({
			headers: { Authorization: `Bearer ${tokenShub}` },
			timeout: 60000
		});
		const response = await reqInstance.get(
			`${constants.SHUB_URL}/local/group-cluster-verify?${queryString}`
		);
		return response.data;
	} catch (error) {
		console.error("PropertyService -> verifyClusterQuotesApi -> error: ", error);
		return {
			success: false,
			error: error?.response?.data?.error || error?.message || 'Verification failed'
		};
	}
};

export const fetchLatestProperties = async () => {
    try {
		const tokenShub = constants.SHUB_STATIC_TOKEN;

		const reqInstance = axios.create({
			headers: {
				Authorization: `Bearer ${tokenShub}`
			}
		});
        const response = await reqInstance.get(constants.SHUB_URL + '/local/latest-listings');
        return response;
    } catch (error) {
        console.error('Error fetching latest properties:', error);
        return { data: [] };
    }
};